import { createClient } from '@supabase/supabase-js';
import * as Sentry from "@sentry/react";

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables');
}

// Create Supabase client with enhanced security configuration
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    flowType: 'pkce',
    storage: window.localStorage,
    storageKey: 'supabase.auth.token',
    debug: import.meta.env.DEV
  },
  global: {
    headers: {
      'X-Client-Info': 'translatenow@1.0.0',
      'X-XSS-Protection': '1; mode=block',
      'X-Content-Type-Options': 'nosniff',
      'Referrer-Policy': 'strict-origin-when-cross-origin'
    }
  },
  realtime: {
    params: {
      eventsPerSecond: 10
    }
  }
});

export async function handleRefreshTokenError(error: any): Promise<boolean> {
  const errMsg = error?.message?.toLowerCase() || '';

  console.warn('Refresh token error encountered:', error);

  if (errMsg.includes('invalid refresh token') || errMsg.includes('expired refresh token')) {
    console.warn('Detected invalid or expired refresh token. Clearing session and redirecting to login.');

    // Clear Supabase client session if applicable
    await supabase.auth.signOut();

    // Clear session data from localStorage
    localStorage.removeItem('supabase.auth.token');
    localStorage.removeItem('supabase.auth.refresh_token');
    localStorage.removeItem('supabase.auth.expires_at');

    // Log info to Sentry
    Sentry.captureMessage(`Refresh token error: ${error.message}`, 'warning');

    // Redirect the user to the login page
    window.location.replace('/auth');

    return true;
  }

  return false;
}

// Test connection and log status
export const testSupabaseConnection = async () => {
  try {
    const { data, error } = await supabase.auth.getSession();
    if (error) {
      Sentry.captureException(error);
      console.error('Supabase connection test failed:', error);
      return false;
    }
    return true;
  } catch (err) {
    Sentry.captureException(err);
    console.error('Failed to connect to Supabase:', err);
    return false;
  }
};

// Initialize connection test
testSupabaseConnection().then(connected => {
  if (!connected) {
    console.warn('⚠️ Supabase connection issues detected');
    Sentry.captureMessage('Supabase connection issues detected', 'warning');
  }
});